import * as React from 'react';
import { styled } from '@glitz/react';
import { translate, useDynamicData, useStaticData } from '@avensia/excite';
import { Document, type StyleDecorators, ContentList } from '@avensia/excite-contentful';
import { Position, useFormatPrice } from '@avensia/excite-ui';
import { isNordicNest, isSvenssons, Theme } from 'webapp/theme';
import { Cart } from 'webapp/pages/checkout/cart';
import { ZIndex } from 'webapp/z-index';
import { TestIdProps } from '@avensia/test-automation';
import { absoluteCenterDecorator } from 'webapp/decorators/center';
import { scrollbarDecorator } from 'webapp/decorators/scrollbar';
import { Flyout } from 'webapp/shared/flyout';
import { useFlyout } from 'webapp/shell/flyout-context';
import { useCustomerType } from '@avensia/excite-commercetools';
import { FreeShippingBubble } from 'webapp/pages/checkout/FreeShippingBubble';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { FieldSize } from 'webapp/components/field-size';
import { Button, ButtonLink } from 'webapp/components/button';
import { CheckIcon, CloseIcon } from 'webapp/components/icons';
import MiniCartNotification from './MiniCartNotification';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import { BANNER_MOBILE_HEIGHT } from 'webapp/utils/constants';
import { Block, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import VerticalCarousel from 'webapp/site-banner/VerticalCarousel';
import { useKlarnaExpressCheckoutAuthorization } from 'webapp/klarna-express-checkout';
import { HydrationOptionsProvider } from '@avensia/excite/progressive-hydration';
import { useNinetailedFeatureTest } from 'webapp/ninetailed/NinetailedProvider';

export default function MiniCart() {
  const checkoutPageUrl = useDynamicData<NordicNest.Api.WellknownPagesApiModel>()[0]?.checkoutPageUrl;
  const [cart] = useDynamicData<Excite.Commercetools.CartApiModel>();
  const { close, isOpen: open } = useFlyout('Cart');
  const formatPrice = useFormatPrice();
  const { isCompanyCustomer } = useCustomerType();
  const { variantKey, componentRef } = useNinetailedFeatureTest<HTMLDivElement>('test-klarna-express');
  const isIngridActive = cart?.viewData?.ingridActive;
  const isVatActive = cart?.viewData?.vatActive;

  const cartUsps = useStaticData<NordicNest.Api.CartUspApiModel>();
  const cartEditorialContent = useStaticData<NordicNest.Api.CartEditorialContentAreaApiModel>();

  useKlarnaExpressCheckoutAuthorization(cart, open);

  const showKlarnaExpress = variantKey !== 'hide-klarna-express';

  return (
    <Flyout zIndex={ZIndex.Overlay} position={Position.Right} visible={open} onClose={close}>
      <Header {...TestIdProps.MiniCartElement}>
        <Heading>{translate(t => t.miniCart.heading)}</Heading>
        <Button
          square
          as={Theme.Transparent}
          onClick={close}
          aria-label={translate(t => t.miniCart.closeMinicart)}
          {...NnTestIdProps.MiniCartCloseButton}
        >
          <CloseIcon />
        </Button>
      </Header>
      <Content>
        <Cart compact />
        {cartEditorialContent.content && (
          <EditorialContentArea>
            <HydrationOptionsProvider renderInsteadOfHydrate>
              <ContentList {...cartEditorialContent.content} />
            </HydrationOptionsProvider>
          </EditorialContentArea>
        )}
      </Content>
      <Footer ref={componentRef}>
        {cart && cart.items.length > 0 && (
          <>
            <Summary>
              {!isIngridActive && cart?.shippingCost && (
                <>
                  <span>{translate(t => t.order.shipping)}</span>
                  <Sum suppressHydrationWarning>
                    {formatPrice(
                      isCompanyCustomer || !isVatActive ? cart.shippingCost.exclVat : cart.shippingCost.inclVat,
                    )}
                  </Sum>
                </>
              )}
              {cart?.totalPrice && cart?.subTotal && (
                <>
                  {isCompanyCustomer || !isVatActive ? (
                    <>
                      <span>
                        {isVatActive
                          ? isIngridActive
                            ? translate(t => t.order.totalExclVatExclShipping)
                            : translate(t => t.order.priceExclVat)
                          : translate(t => t.order.total)}
                      </span>
                      <TotalPrice suppressHydrationWarning>
                        {isIngridActive ? formatPrice(cart.subTotal.exclVat) : formatPrice(cart.totalPrice.exclVat)}
                      </TotalPrice>
                    </>
                  ) : (
                    <>
                      <span>
                        {isIngridActive
                          ? translate(t => t.order.totalInclVatExclShipping)
                          : translate(t => t.order.priceInclVat)}
                      </span>
                      <TotalPrice suppressHydrationWarning>
                        {isIngridActive ? formatPrice(cart.subTotal.inclVat) : formatPrice(cart.totalPrice.inclVat)}
                      </TotalPrice>
                    </>
                  )}
                </>
              )}
            </Summary>
            <BubbleWrapper>
              <FreeShippingBubble />
            </BubbleWrapper>
            <MiniCartNotification />
          </>
        )}
        <ButtonLink
          size={isSvenssons ? FieldSize.Large : FieldSize.Default}
          as={Theme.ActionButton}
          to={checkoutPageUrl ?? '#'}
          disabled={!checkoutPageUrl}
          {...TestIdProps.goToCheckoutButton}
        >
          {translate(t => t.miniCart.toCheckout)}
        </ButtonLink>
        {cart && cart.items.length > 0 && showKlarnaExpress && (
          <KlarnaExpressCheckoutButton id="klarna-express-checkout" />
        )}
        {cartUsps?.messages.length > 0 && (
          <UspContent>
            <Messages isAnimating={open}>
              {cartUsps?.messages.map((message, index) => (
                <Message key={index} css={{ color: message?.textColor ?? 'inherit' }}>
                  <Check />
                  <Document styleDecorators={styles} {...message.content} />
                </Message>
              ))}
            </Messages>
          </UspContent>
        )}
      </Footer>
    </Flyout>
  );
}

const styles: StyleDecorators = {
  p: styled(createMinLargeViewportDecorator({ marginBottom: 0 }), {
    marginBottom: 0,
    fontSize: t => t.sharedFontSize.xxs,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  }),
};

const BubbleWrapper = styled.div({
  width: '100%',
});

const Header = styled.header(
  {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    paddingLeft: t => t.sharedSpacing.l,
    height: 64,
  },
  isSvenssons
    ? {
        position: 'relative',
        justifyContent: 'flex-end',
        backgroundColor: t => t.monochrome.color,
        color: t => t.monochrome.text,
      }
    : {
        justifyContent: 'space-between',
        border: { bottom: { width: 'thin', style: 'solid', color: t => t.monochromeLight } },
      },
);

const Heading = styled.span(isSvenssons && absoluteCenterDecorator, {
  fontSize: t => t.sharedFontSize.m,
});

const KlarnaExpressCheckoutButton = styled.div({
  width: '100%',
  marginTop: x => x.spacing.m,
});

const Content = styled.div(scrollbarDecorator, {
  flexGrow: 1,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
});

const EditorialContentArea = styled.div({
  marginTop: 'auto',
  paddingTop: t => t.sharedSpacing.xl,
});

const Footer = styled.footer({
  flexShrink: 0,
  padding: { x: t => t.sharedSpacing.m, y: t => t.sharedSpacing.l },
  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 15px 40px 5px',
});

const Summary = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  margin: { bottom: t => t.sharedSpacing.l },
});

const Sum = styled.span({ textAlign: 'right' });

const TotalPrice = styled(Sum, {
  fontSize: t => t.sharedFontSize.m,
  fontWeight: 'bold',
});

const UspContent = styled(Block, maxContentNormalWidthDecorator, {
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  height: BANNER_MOBILE_HEIGHT,
  marginTop: t => t.sharedSpacing.l,
  zIndex: ZIndex.Overlay,
});

const Messages = styled(VerticalCarousel, {
  position: 'relative',
  display: 'flex',
  height: 'inherit',
  justifyContent: 'center',
  width: '100%',
  overflow: 'hidden',
});

const Message = styled.div({
  opacity: 0,
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  transform: 'translateY(50%)',
  willChange: 'transform, opacity',
  pointerEvents: 'none',
  height: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: t => t.sharedFontSize.xxs,
  gap: t => t.sharedSpacing.xs,
});

const Check = styled(CheckIcon, {
  width: isNordicNest ? 24 : 14,
  height: isNordicNest ? 24 : 14,
  flexShrink: 0,
});
